import { useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import {
  Button,
  Input,
  Dialog,
  DialogHeader,
  DialogBody,
} from "@material-tailwind/react";
import { AiFillDollarCircle } from "react-icons/ai";
import { BiLogoGmail } from "react-icons/bi";
import {
  FaFacebook,
  FaLinkedin,
  FaExternalLinkSquareAlt,
  FaLink,
  FaGoogleDrive,
  FaAddressCard,
  FaRupeeSign,
} from "react-icons/fa";
import { FaSquarePinterest } from "react-icons/fa6";
import { IoCallSharp, IoLogoWhatsapp, IoDocumentText } from "react-icons/io5";
import { MdMail, MdPhoto, MdPayments, MdDeleteOutline } from "react-icons/md";
import { PiGlobeBold } from "react-icons/pi";
import {
  RiMessage2Fill,
  RiInstagramFill,
  RiTwitterXFill,
  RiFolderVideoFill,
  RiCloseCircleFill,
} from "react-icons/ri";
import { toast } from "react-toastify";

import { aboutApp } from "../../config/appConfig.js";
import { defaultColors } from "../../config/mylinksConfig.js";
import { awsBucketConfig } from "../../config/awsBucketConfig.js";
import { useMyLinksContext } from "../../contexts/MyLinksContextProvider";
import { isWebLink } from "../utils/inputValidations";
import { copyToClipboard } from "../utils/copyToClipboard";
import {
  uploadMediaOnAWS,
  EnumFileUploadStatus,
} from "../../helpers/AWSService.js";

const ManageMyLinks = () => {
  const {
    createMyLinksLink,
    getMyLinksLink,
    removeMyLinksLink,
    getMyLinksProfileName,
    myLinksProfileName,
    myLinksLinks,
    setMyLinksLinks,
    isMyLinksEnabled,
    enableDisableMyLink,
    setIsMyLinksEnabled,
    updateMyLinkProfilePicture,
    updateMyLinkThemeColor,
  } = useMyLinksContext();

  const iconComponents = {
    PiGlobeBold,
    IoCallSharp,
    RiMessage2Fill,
    MdMail,
    IoLogoWhatsapp,
    FaFacebook,
    RiInstagramFill,
    RiTwitterXFill,
    FaLinkedin,
    FaSquarePinterest,
    FaExternalLinkSquareAlt,
    FaLink,
    BiLogoGmail,
    FaGoogleDrive,
    MdPhoto,
    RiFolderVideoFill,
    IoDocumentText,
    MdPayments,
    FaRupeeSign,
    AiFillDollarCircle,
    FaAddressCard,
  };

  const iconsList = [
    { id: "AiFillDollarCircle", icon: "AiFillDollarCircle", lib: "ai" },
    { id: "BiLogoGmail", icon: "BiLogoGmail", lib: "bi" },
    { id: "FaFacebook", icon: "FaFacebook", lib: "fa" },
    { id: "FaLinkedin", icon: "FaLinkedin", lib: "fa" },
    {
      id: "FaExternalLinkSquareAlt",
      icon: "FaExternalLinkSquareAlt",
      lib: "fa",
    },
    { id: "FaLink", icon: "FaLink", lib: "fa" },
    { id: "FaGoogleDrive", icon: "FaGoogleDrive", lib: "fa" },
    { id: "FaAddressCard", icon: "FaAddressCard", lib: "fa" },
    { id: "FaRupeeSign", icon: "FaRupeeSign", lib: "fa" },
    { id: "FaSquarePinterest", icon: "FaSquarePinterest", lib: "fa6" },
    { id: "IoCallSharp", icon: "IoCallSharp", lib: "io5" },
    { id: "IoLogoWhatsapp", icon: "IoLogoWhatsapp", lib: "io5" },
    { id: "IoDocumentText", icon: "IoDocumentText", lib: "io5" },
    { id: "MdMail", icon: "MdMail", lib: "md" },
    { id: "MdPhoto", icon: "MdPhoto", lib: "md" },
    { id: "MdPayments", icon: "MdPayments", lib: "md" },
    { id: "PiGlobeBold", icon: "PiGlobeBold", lib: "pi" },
    { id: "RiMessage2Fill", icon: "RiMessage2Fill", lib: "ri" },
    { id: "RiInstagramFill", icon: "RiInstagramFill", lib: "ri" },
    { id: "RiTwitterXFill", icon: "RiTwitterXFill", lib: "ri" },
    { id: "RiFolderVideoFill", icon: "RiFolderVideoFill", lib: "ri" },
  ];

  const liveUrl = `${aboutApp.myLinksBaseUrl}/${myLinksProfileName}`;

  // TBI. Currently profile name is same as corporate ID. Might get implemented later.
  // const [newProfileName, setNewProfileName] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [pageUrl, setPageUrl] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [draggingItem, setDraggingItem] = useState(null);
  const [refreshPageCounter, setRefreshPageCounter] = useState(0);
  const [selectedIcon, setSelectedIcon] = useState("");
  const [colors, setColors] = useState(defaultColors);

  const handleColorChange = (key, value) => {
    setColors((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveThemeColor = async () => {
    await updateMyLinkThemeColor(colors);
  };

  const resetStates = () => {
    setOpenDialog(false);
    setPageUrl("");
    setPageTitle("");
    setDraggingItem(null);
    setRefreshPageCounter(0);
    setSelectedIcon("");
  };

  const createLinkOnSubmit = async (e) => {
    e.preventDefault();

    if (!isWebLink(pageUrl)) return toast.warn("Please type a valid web url");

    await createMyLinksLink({
      profileName: myLinksProfileName,
      pageLink: pageUrl,
      pageTitle: pageTitle,
      thumbnailLink: selectedIcon === "" ? "FaJsfiddle" : selectedIcon,
    });
    setOpenDialog(false);
    setRefreshPageCounter((prvCounter) => prvCounter + 1);
    setPageUrl("");
    setPageTitle("");
    setDraggingItem(null);
    setSelectedIcon("");
  };

  const [preview, setPreview] = useState(null);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const result = await uploadMedia(file);
      if (result === "") {
        return toast.error("Error uploading profile picture. Try again later.");
      } else {
        setPreview(URL.createObjectURL(file));
        const profilePictureResult = await updateMyLinkProfilePicture(result);
        if (!profilePictureResult) {
          return toast.error(
            "Error uploading profile picture. Try again later."
          );
        }
      }
    }
  };

  const uploadMedia = async (uploadedFile) => {
    try {
      const uniqueId = uuid();
      const uniqueFileName =
        uniqueId + "." + uploadedFile?.name.split(".").pop().toLowerCase();
      const uploadParams = {
        fileName: uniqueFileName,
        mediaFile: uploadedFile,
        fileType: uploadedFile?.type,
      };

      const response = await uploadMediaOnAWS(uploadParams);
      if (response === EnumFileUploadStatus.FileUploadSuccess) {
        return uniqueFileName;
      } else {
        return "";
      }
    } catch (err) {
      console.log("profile picture uploadMedia err", err);
      return "";
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
    // Directly use the element as drag image
    const node = e.target;
    e.dataTransfer.setDragImage(node, 0, 0);
    setDraggingItem(index);
  };

  const toggleMyLink = async (isEnable) => {
    await enableDisableMyLink(isEnable);
    setRefreshPageCounter((prvCounter) => prvCounter + 1);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    // const draggedIndex = e.dataTransfer.getData("text/plain");
    //setToReorderMessages(true);

    const updatedItems = [...myLinksLinks];
    const draggedItem = updatedItems[draggingItem];

    // Remove the item from its original position
    updatedItems.splice(draggingItem, 1);

    // Insert the item at the new index
    updatedItems.splice(index, 0, draggedItem);

    setDraggingItem(null);
    setMyLinksLinks(updatedItems);
  };

  const deleteMyLinksLink = async (e, webLink) => {
    e.preventDefault();
    await removeMyLinksLink(webLink);
    setRefreshPageCounter((prvCounter) => prvCounter + 1);
  };

  const IconDropdown = () => {
    const selectIconOnClick = (e, icon) => {
      e.preventDefault();
      setSelectedIcon(icon);
    };

    return (
      <>
        <div>Select Thumbnail</div>
        <div className="grid grid-cols-12 gap-2">
          {iconsList.map((item) => {
            const IconComponent = iconComponents[item.icon];
            if (!IconComponent) return null;

            return (
              <div
                key={item.id}
                onClick={(e) => selectIconOnClick(e, item.icon)}
              >
                <IconComponent
                  className={`my-hover-effect w-7 h-7 cursor-pointer ${
                    selectedIcon === item.icon && "text-green-500"
                  }`}
                />
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const DisplayIcon = ({ selectedIcon }) => {
    if (!selectedIcon) return null;

    const IconComponent = iconComponents[selectedIcon];
    if (!IconComponent) return null;

    return <IconComponent size={30} className="my-hover-effect" />;
  };

  const loadMyLinksData = async () => {
    const result = await getMyLinksLink();
    console.log("loadMyLinksData result", result);
    if (result) {
      setColors(result?.[0]?.themeColor || defaultColors);
    }
  };

  useEffect(() => {
    loadMyLinksData();
    getMyLinksProfileName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshPageCounter]);

  return (
    <main className="w-full mt-5 mb-10 lg:my-1 flex justify-between gap-5">
      <div className="w-full space-y-5">
        <div className="w-full space-y-2">
          <h1 className="text-2xl">MyLinks</h1>
          <h3>Create, Delete and Manage MyLinks Page.</h3>
        </div>
        <div className="flex flex-row justify-start gap-4">
          <div className="space-y-1">
            <p className="text-sm">Live Link</p>
            {!myLinksProfileName || myLinksProfileName === "" ? (
              <p className="bg-red-500 w-fit px-2 text-white text-sm">
                Link Not Generated Yet
              </p>
            ) : (
              <span
                onClick={() => copyToClipboard(liveUrl)}
                className="text-blue-700"
              >
                {liveUrl}
              </span>
            )}
          </div>
          <input
            type="checkbox"
            role="switch"
            onChange={() => {
              setIsMyLinksEnabled((prevState) => {
                const newState = !prevState;
                toggleMyLink(newState);
                return newState;
              });
            }}
            checked={isMyLinksEnabled === 1 ? true : false}
            className="my-toggle-switch"
          />
        </div>
        <div className="bg-slate-300 w-full h-[2px] rounded" />
        <div className="flex flex-row items-center gap-4 p-1 w-full">
          {myLinksProfileName?.length > 0 && (
            <Button
              type="button"
              variant="gradient"
              color="blue"
              disabled={!isMyLinksEnabled}
              onClick={() => {
                setSelectedIcon("FaJsfiddle");
                setOpenDialog(true);
              }}
              className="w-28"
            >
              Add Link
            </Button>
          )}
        </div>

        <div className="grid grid-cols-5 w-full">
          <div className="my-card-style-1 min-h-[65vh] mr-2 col-span-3 space-y-1">
            <h4 className="font-bold">List of Links</h4>
            <p className="mb-5 text-sm">
              You can drag and drop to change the links order.
            </p>

            {Array.isArray(myLinksLinks?.[0]?.linksDetails) &&
              myLinksLinks?.[0]?.linksDetails?.map((item, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, index)}
                  className="my-smooth-transition-1 bg-slate-50 px-2 py-1 flex justify-between gap-2 rounded-md hover:cursor-move"
                >
                  <div className="flex justify-start gap-3">
                    <DisplayIcon selectedIcon={item?.thumbnailLink} />
                    <div className="">
                      <p className="font-bold text-sm">{item?.pageTitle}</p>
                      <p className="text-sm">{item?.pageLink}</p>
                    </div>
                  </div>

                  <button
                    onClick={(e) => deleteMyLinksLink(e, item?.pageLink)}
                    className="my-button-style-cancel"
                  >
                    <MdDeleteOutline />
                  </button>
                </div>
              ))}
          </div>
          <div className="col-span-2 w-full my-card-style-1 min-h-[55vh] p-5 space-y-2">
            {/* Profile Image Circle */}
            <div className="flex flex-col justify-center gap-2 item-center">
              <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-gray-300 shadow self-center">
                <img
                  src={
                    preview ||
                    (myLinksLinks?.[0]?.profilePicture
                      ? `${awsBucketConfig.openUploadedFilePrefix}${myLinksLinks?.[0]?.profilePicture}`
                      : "https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff") ||
                    "https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff"
                  }
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Browse Button */}
              <label className="px-4 py-2 bg-blue-600 text-white self-center rounded-lg shadow cursor-pointer hover:bg-blue-700 transition">
                Browse
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
            <div className="p-6 max-w-lg mx-auto bg-gray-50 rounded-xl shadow-md space-y-3">
              <h2 className="text-xl font-bold">Theme Editor</h2>

              <div>
                {Object.entries(colors).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between space-x-4"
                  >
                    <label className="uppercase">
                      {key.replace(/_/g, " ")}
                    </label>
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="w-12 h-8 cursor-pointer"
                    />
                  </div>
                ))}
                <button
                  onClick={handleSaveThemeColor}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Theme
                </button>
              </div>

              {/* Preview Area */}
              <div
                className="p-4 rounded-lg"
                style={{
                  background: colors.bg_primary_color,
                  color: colors.text_color_paragraph,
                }}
              >
                <h3 style={{ color: colors.text_color_heading }}>
                  Heading Text
                </h3>
                <p style={{ color: colors.text_color_subheading }}>
                  Subheading text
                </p>
                <p>Paragraph text with selected theme colors.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TBI. Preview hidden for now */}
      {/* <div className="my-card-style-1 md:w-[35vw] lg:w-[50vw] min-h-screen mx-auto space-y-5">
        <img
          src={wbProfileInfo?.profilePic ? wbProfileInfo?.profilePic : userPic}
          alt="WaBM MyLinks Profile Pic"
          className="w-36 h-36 mx-auto rounded-full"
        />
        <div className="font-bold text-center">{myLinksProfileName}</div>
        <div className="space-y-1">
          {Array.isArray(myLinksLinks?.[0]?.linksDetails) &&
            myLinksLinks?.[0]?.linksDetails?.map((item, index) => (
              <a
                key={index}
                href={item?.pageLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="my-button-style-3 text-black text-center text-xs">
                  <DisplayIcon selectedIcon={item?.thumbnailLink} />
                  <p>{item?.pageTitle}</p>
                </div>
              </a>
            ))}
        </div>
      </div> */}

      <Dialog open={openDialog} size="sm" className="z-10">
        <DialogHeader>
          <div className="flex justify-between w-full">
            <div className="text-wabmBlue font-bold text-lg self-start">
              Add a Link
            </div>
            <RiCloseCircleFill
              onClick={() => resetStates()}
              size={30}
              className="text-red-500 hover:text-red-600 transition hover:cursor-pointer duration-300 ease-in-out self-end"
            />
          </div>
        </DialogHeader>
        <DialogBody>
          <form
            onSubmit={(e) => createLinkOnSubmit(e)}
            className="flex flex-col gap-5 justify-start"
          >
            <Input
              type="text"
              variant="standard"
              required
              label="Page title"
              placeholder="Ex. Instagram"
              value={pageTitle}
              onChange={(e) => setPageTitle(e.target.value)}
              className="h-7 text-lg"
            />
            <Input
              type="text"
              variant="standard"
              required
              label="Web Link"
              placeholder="www.instagram.com/wabm.in"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value.trim())}
              className="h-7 text-lg"
            />
            <IconDropdown />
            <div className="flex flex-row-reverse justify-between">
              <Button
                type="submit"
                className="self-end"
                variant="filled"
                color="green"
              >
                Confirm
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>
    </main>
  );
};

export default ManageMyLinks;
